import { useState } from 'react';
import type { Book, ShelfStatus } from '@booknest/shared';

import { useAddToShelf } from '../hooks/useAddToShelf.js';

import { BookCover } from './BookCover.js';

const STATUS_BUTTONS: ReadonlyArray<{ status: ShelfStatus; icon: string; label: string }> = [
  { status: 'wishlist', icon: '✨', label: 'Wishlist' },
  { status: 'reading', icon: '📖', label: 'En cours' },
  { status: 'done', icon: '✅', label: 'Terminé' },
  { status: 'abandoned', icon: '🍂', label: 'Abandonné' },
];

interface BookSuggestionRowProps {
  book: Book;
  onAdded?: () => void;
}

export function BookSuggestionRow({ book, onAdded }: BookSuggestionRowProps): JSX.Element {
  const mutation = useAddToShelf();
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const handleAdd = (status: ShelfStatus): void => {
    setErrorCode(null);
    mutation.mutate(
      { bookId: book.id, status },
      {
        onSuccess: () => onAdded?.(),
        onError: (err) => setErrorCode(extractErrorCode(err)),
      },
    );
  };

  return (
    <li className="flex items-start gap-3 border-b border-cream-dark px-3 py-2 last:border-0 hover:bg-cream-dark/40">
      <BookCover
        url={book.coverUrl}
        title={book.title}
        book={book}
        className="w-10 flex-shrink-0"
      />
      <div className="flex flex-1 flex-col gap-1 overflow-hidden">
        <p className="truncate font-serif text-sm font-semibold text-brown">{book.title}</p>
        <p className="truncate text-xs text-brown-soft">
          {book.author}
          {book.year ? ` · ${book.year}` : ''}
        </p>
        {mutation.isSuccess ? (
          <p className="text-xs font-semibold text-terracotta">Ajouté ✓</p>
        ) : errorCode === 'BOOK_ALREADY_IN_SHELF' ? (
          <p className="text-xs text-brown-soft">Déjà dans ta bibliothèque</p>
        ) : errorCode ? (
          <p className="text-xs text-red-500">Erreur ({errorCode})</p>
        ) : null}
      </div>
      <div className="flex flex-shrink-0 gap-1">
        {STATUS_BUTTONS.map((b) => (
          <button
            key={b.status}
            type="button"
            title={b.label}
            aria-label={`Ajouter à : ${b.label}`}
            disabled={mutation.isPending || mutation.isSuccess}
            onClick={(e) => {
              e.stopPropagation();
              handleAdd(b.status);
            }}
            className="rounded-full bg-paper px-2 py-1 text-base ring-1 ring-cream-dark transition-colors hover:bg-cream-dark disabled:opacity-50"
          >
            {b.icon}
          </button>
        ))}
      </div>
    </li>
  );
}

function extractErrorCode(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { code?: unknown } } }).response;
    if (response?.data && typeof response.data === 'object' && 'code' in response.data) {
      const code = response.data.code;
      if (typeof code === 'string') return code;
    }
  }
  return 'UNKNOWN';
}
